/**
 * Full-page Help & Support — combines: FAQ browser, "start a chat" CTA that
 * opens the SupportWidget, past case history for the signed-in user.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, ChevronRight, MessageSquare, Bug, Mail, ShieldAlert } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";

const CATEGORY_LABELS = {
  features: "Features",
  plans_pricing: "Plans & Pricing",
  account_login: "Account & Login",
  billing_payments: "Billing & Payments",
  resume: "Resume tools",
  roadmap: "Career Roadmap",
  interview_simulator: "Interview Simulator",
  voice_interview: "Live Voice Interview",
  financial_planning: "Financial Planning",
  salary_intel: "Salary Intelligence",
  assessments: "Assessments",
  portfolio: "Portfolio",
  notifications: "Notifications",
  privacy_security: "Privacy & Security",
  known_issues: "Known issues",
  escalation: "Contact",
};

export default function Support() {
  const [kb, setKB] = useState({ entries: [], categories: [] });
  const [q, setQ] = useState("");
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/support/kb").then((r) => setKB(r.data)).catch(() => {});
    api.get("/support/cases").then((r) => setCases(r.data.cases || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q) return kb.entries;
    const ql = q.toLowerCase();
    return kb.entries.filter((e) =>
      e.title.toLowerCase().includes(ql) ||
      e.body.toLowerCase().includes(ql) ||
      (e.tags || []).some((t) => t.toLowerCase().includes(ql))
    );
  }, [q, kb.entries]);

  const byCategory = useMemo(() => {
    const out = {};
    filtered.forEach((e) => {
      out[e.category] = out[e.category] || [];
      out[e.category].push(e);
    });
    return out;
  }, [filtered]);

  const openWidget = () => {
    localStorage.setItem("careeros-support-open", "1");
    window.dispatchEvent(new StorageEvent("storage", { key: "careeros-support-open", newValue: "1" }));
    // The widget listens to open state via its own useEffect; nudge with a reload of state
    window.location.reload();
  };

  return (
    <div className="max-w-5xl" data-testid="support-page">
      <PageHeader
        eyebrow="Help & Support"
        title="How can we help?"
        subtitle="Browse frequent questions, chat with our AI support agent, or send us a message directly."
      />

      {/* Quick actions */}
      <div className="grid md:grid-cols-3 gap-3 mb-8">
        <ActionCard
          testid="action-chat"
          icon={<MessageSquare size={18} />}
          title="Chat with support"
          subtitle="AI agent · instant help"
          onClick={openWidget}
        />
        <ActionCard
          testid="action-bug"
          icon={<Bug size={18} />}
          title="Report a bug"
          subtitle="We'll investigate quickly"
          onClick={openWidget}
        />
        <ActionCard
          testid="action-email"
          icon={<Mail size={18} />}
          title="Email us"
          subtitle="info@careeros-ai.com"
          href="mailto:info@careeros-ai.com"
        />
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search help articles…"
          data-testid="support-search"
          className="w-full pl-10 pr-3 py-2.5 bg-background border border-border rounded-lg text-sm outline-none focus:border-primary"
        />
      </div>

      {/* KB grouped by category */}
      {Object.keys(byCategory).length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-12">
          No help articles match your search. <button onClick={openWidget} className="text-primary underline">Chat with support</button>.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(byCategory).map(([cat, entries]) => (
            <section key={cat} data-testid={`kb-cat-${cat}`}>
              <h2 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
                {CATEGORY_LABELS[cat] || cat}
              </h2>
              <div className="grid gap-2">
                {entries.map((e) => (
                  <button
                    key={e.id}
                    data-testid={`kb-entry-${e.id}`}
                    onClick={() => setSelected(e)}
                    className="text-left bg-card border border-border rounded-lg p-4 hover:border-primary/40 transition-colors flex items-start gap-3"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm">{e.title}</div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.body}</div>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Past cases */}
      {cases.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Your past cases</h2>
          <div className="grid gap-2">
            {cases.map((c) => <CaseRow key={c.case_id} c={c} />)}
          </div>
        </section>
      )}

      {/* Slide-in article view */}
      {selected && <ArticleModal entry={selected} onClose={() => setSelected(null)} onChat={openWidget} />}
    </div>
  );
}

function ActionCard({ icon, title, subtitle, onClick, href, testid }) {
  const Comp = href ? "a" : "button";
  return (
    <Comp
      data-testid={testid}
      href={href}
      onClick={onClick}
      className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/40 transition-colors flex items-center gap-3"
    >
      <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center">{icon}</div>
      <div>
        <div className="font-medium text-sm">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </Comp>
  );
}

function CaseRow({ c }) {
  const statusColor = ({
    open: "text-blue-500",
    escalated: "text-[hsl(var(--warning))]",
    resolved: "text-[hsl(var(--success))]",
    unresolved: "text-destructive",
    closed: "text-muted-foreground",
  })[c.status] || "text-muted-foreground";
  return (
    <Link
      to={`/app/support/case/${c.case_id}`}
      data-testid={`case-row-${c.case_id}`}
      className="bg-card border border-border rounded-lg p-3 flex items-center gap-3 hover:border-primary/40"
    >
      <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
        {c.status === "escalated" ? <ShieldAlert size={14} /> : <MessageSquare size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{c.summary || "(no summary)"}</div>
        <div className="text-xs text-muted-foreground">
          {c.case_id} · {c.category} · {new Date(c.created_at).toLocaleDateString()}
        </div>
      </div>
      <div className={`text-xs font-mono uppercase ${statusColor}`}>{c.status}</div>
    </Link>
  );
}

function ArticleModal({ entry, onClose, onChat }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">
          {CATEGORY_LABELS[entry.category] || entry.category}
        </div>
        <h2 className="text-xl font-display font-bold mb-4">{entry.title}</h2>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{entry.body}</div>
        <div className="mt-6 flex gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={onChat}>Still stuck? Chat with support</Button>
        </div>
      </div>
    </div>
  );
}
